<template>
  <div :class="componentClassName">
    <div class="m-audio-player-control">
      <a-audio-player-progress-bar :start="elapsedTime" :time="totalTime" @progress="handleProgress" />
      <a-audio-player-control
        :is-playing="isPlaying"
        @play="handlePlay"
        @pause="handlePause"
        @forward="handleForward"
        @backward="handleBackward"
      />
    </div>
    <div class="m-audio-player__audio">
      <audio ref="audio">
        <source :src="audioUrl" :type="audioType" />
        Your browser does not support the audio tag.
      </audio>
    </div>
  </div>
</template>

<script>
import { mapModifiers } from '@/libs/component';
import { convertSecondsToElapsedTime, convertElapsedTimeToSeconds } from '@/libs/utils';
import AAudioPlayerControl from '@/components/atoms/audio-player-control';
import AAudioPlayerProgressBar from '@/components/atoms/audio-player-progress-bar';

export default {
  name: 'm-audio-player',
  components: {
    AAudioPlayerControl,
    AAudioPlayerProgressBar,
  },
  props: {
    class: {
      type: String,
      default: '',
    },
    audioUrl: {
      type: String,
      default: null,
    },
    audioType: {
      type: String,
      default: 'audio/mpeg', // MP3
    },
    autoplay: {
      type: Boolean,
      default: null,
    },
    startTime: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      audioLoaded: false,
      audioDuration: 0,
      isPlaying: false,
      listenerActive: false,
      playbackTime: 0,
    };
  },
  computed: {
    componentClassName() {
      const baseClassName = mapModifiers('m-audio-player');
      return `${baseClassName} ${this.class}`.trim();
    },
    totalTime() {
      if (this.audioDuration) {
        return convertSecondsToElapsedTime(this.audioDuration);
      }
      return '00:00';
    },
    elapsedTime() {
      const seconds = this.playbackTime;
      return convertSecondsToElapsedTime(seconds);
    },
  },
  watch: {
    audioUrl(val) {
      this.initializeAudio();
      if (this.autoplay) {
        this.handlePlay();
      }
    },
  },
  mounted() {
    if (this.audioUrl) {
      this.initializeAudio();
    }
  },
  beforeUnmount() {
    // this.cleanupListeners();
    this.uninitializeAudio();
  },
  methods: {
    initializeAudio() {
      this.uninitializeAudio();
      const audio = this.$refs.audio;
      audio.addEventListener('loadedmetadata', this.setupProgress);
      audio.addEventListener('canplay', this.setupPlayer);
      audio.addEventListener('timeupdate', this.playbackListener);
    },
    uninitializeAudio() {
      this.audioLoaded = false;
      const audio = this.$refs.audio;
      audio.removeEventListener('loadedmetadata', this.setupProgress);
      audio.removeEventListener('canplay', this.setupPlayer);
      audio.removeEventListener('timeupdate', this.playbackListener);
      audio.removeEventListener('ended', this.endListener);
      audio.removeEventListener('pause', this.pauseListener);
    },
    setupProgress() {
      const audio = this.$refs.audio;
      this.audioDuration = Math.floor(audio.duration);
    },
    setupPlayer() {
      this.audioLoaded = true;
    },
    playbackListener(e) {
      const audio = this.$refs.audio;
      this.playbackTime = audio.currentTime;
      audio.addEventListener('ended', this.endListener);
      audio.addEventListener('pause', this.pauseListener);
    },
    pauseListener(e) {
      this.isPlaying = false;
      this.listenerActive = false;
      // this.cleanupListeners();
    },
    endListener(e) {
      this.isPlaying = false;
      this.listenerActive = false;
      // this.cleanupListeners();
    },
    /* cleanupListeners(e) {
      const audio = this.$refs.audio;
      audio.removeEventListener('timeupdate', this.playbackListener);
      audio.removeEventListener('ended', this.endListener);
      audio.removeEventListener('pause', this.pauseListener);
    }, */
    // control
    handlePlay() {
      const audio = this.$refs.audio;
      if (this.playbackTime === 0) {
        audio.currentTime = this.startTime;
      } else {
        audio.currentTime = this.playbackTime;
      }
      audio.play();
      this.isPlaying = true;
    },
    handlePause() {
      const audio = this.$refs.audio;
      audio.pause();
      this.isPlaying = false;
    },
    handleForward() {
      const audio = this.$refs.audio;
      if (audio) {
        audio.currentTime += 15;
      }
    },
    handleBackward() {
      const audio = this.$refs.audio;
      if (audio) {
        audio.currentTime -= 15;
      }
    },
    handleProgress(data) {
      const seconds = convertElapsedTimeToSeconds(data.current);
      const audio = this.$refs.audio;
      if (audio) {
        audio.currentTime = seconds;
      }
    },
  },
};
</script>
